import React, { useRef, useCallback, useEffect } from "react";
import { FieldProps } from "formik";
import { TextField } from "./FormFields";

interface TextFieldProps {
    placeholder?: string;
    size?: "small" | "medium" | "big" | "big-with-border";
    numberOfLines?: number;
    style?: React.CSSProperties;
}

interface AutoResizeTextFieldProps extends FieldProps, TextFieldProps {
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    value: string;
}

const AutoResizeTextField: React.FC<AutoResizeTextFieldProps> = ({
    field,
    form,
    meta,
    placeholder,
    size = "small",
    numberOfLines,
    style,
    onChange,
    value,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // Auto-resize textarea based on content
    const autoResizeTextarea = useCallback(() => {
        if (containerRef.current) {
            const textarea = containerRef.current.querySelector("textarea.text-input-big") as HTMLTextAreaElement;
            if (textarea) {
                // Reset height to auto to get the correct scrollHeight
                textarea.style.height = "auto";
                // Set height to scrollHeight to fit content
                textarea.style.height = `${textarea.scrollHeight}px`;
            }
        }
    }, []);

    useEffect(() => {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
            autoResizeTextarea();
        });
    }, [value, autoResizeTextarea]);

    // Also resize on mount
    useEffect(() => {
        requestAnimationFrame(() => {
            autoResizeTextarea();
        });
    }, [autoResizeTextarea]);

    return (
        <div ref={containerRef}>
            <TextField
                field={field}
                form={form}
                meta={meta}
                placeholder={placeholder}
                size={size}
                numberOfLines={numberOfLines}
                style={style}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    onChange(e);
                    // Auto-resize on change
                    const textarea = e.target as HTMLTextAreaElement;
                    if (textarea) {
                        textarea.style.height = "auto";
                        textarea.style.height = `${textarea.scrollHeight}px`;
                    }
                }}
            />
        </div>
    );
};

export default AutoResizeTextField;
